import { Box, Divider, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { InvestmentFinancialMetricsDTO } from '@inversor-hub/types';
import { formatCurrency } from '@/utils/misc';
import { NOI_LABEL } from '../shared/metric-glossary';
import { cardSurface } from '../shared/surfaces';

interface CashflowWaterfallProps {
  metrics: InvestmentFinancialMetricsDTO;
  scaleAmount: (cents: number) => number;
}

type Step = {
  label: string;
  help?: string;
  amount: number;
  sign: '+' | '−';
  kind: 'step' | 'subtotal';
};

export const CashflowWaterfall = ({ metrics, scaleAmount }: CashflowWaterfallProps) => {
  const value = (key: keyof InvestmentFinancialMetricsDTO['metrics']) =>
    scaleAmount(metrics.metrics[key].value ?? 0);

  const steps: Step[] = [
    { label: 'Ingresos del alquiler', amount: value('operatingIncome'), sign: '+', kind: 'step' },
    { label: 'Gastos operativos', amount: value('operatingExpenses'), sign: '−', kind: 'step' },
    { label: NOI_LABEL, amount: value('cashNoi'), sign: '+', kind: 'subtotal' },
    { label: 'Intereses pagados', amount: value('interestPaid'), sign: '−', kind: 'step' },
    { label: 'Principal amortizado', amount: value('principalRepaid'), sign: '−', kind: 'step' },
    {
      label: 'Cashflow después de deuda',
      amount: value('cashflowAfterDebt'),
      sign: '+',
      kind: 'subtotal',
    },
    { label: 'Reformas y mejoras (capex)', amount: value('capexPaid'), sign: '−', kind: 'step' },
    {
      label: 'Cashflow después de capex',
      amount: value('cashflowAfterCapex'),
      sign: '+',
      kind: 'subtotal',
    },
  ];

  const scale = Math.max(...steps.map((step) => Math.abs(step.amount)), 1);
  const otherReceipts = value('otherReceipts');
  const otherPayments = value('otherPayments');

  return (
    <Paper elevation={0} sx={{ p: 3, height: '100%', ...cardSurface }}>
      <Typography variant="h6" fontWeight={600}>
        De los ingresos al bolsillo
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Qué queda del alquiler después de los gastos, la hipoteca y las reformas.
      </Typography>
      <Divider sx={{ mb: 2.5 }} />

      <Stack spacing={2}>
        {steps.map((step) => {
          const isSubtotal = step.kind === 'subtotal';
          const isNegativeResult = isSubtotal && step.amount < 0;
          const color = isSubtotal
            ? isNegativeResult
              ? 'error'
              : 'success'
            : step.sign === '+'
              ? 'success'
              : 'warning';

          return (
            <Box key={step.label}>
              <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
                <Typography
                  variant={isSubtotal ? 'subtitle2' : 'body2'}
                  fontWeight={isSubtotal ? 700 : 500}
                  color={isSubtotal ? 'text.primary' : 'text.secondary'}
                >
                  {isSubtotal ? '= ' : `${step.sign} `}
                  {step.label}
                </Typography>
                <Typography
                  variant={isSubtotal ? 'subtitle1' : 'body2'}
                  fontWeight={isSubtotal ? 700 : 600}
                  color={isNegativeResult ? 'error.main' : undefined}
                >
                  {formatCurrency(step.amount)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min((Math.abs(step.amount) / scale) * 100, 100)}
                color={color}
                sx={{ height: isSubtotal ? 8 : 5, borderRadius: 4 }}
                aria-label={`${step.label}: ${formatCurrency(step.amount)}`}
              />
            </Box>
          );
        })}
      </Stack>

      {(otherReceipts > 0 || otherPayments > 0) && (
        <>
          <Divider sx={{ my: 2.5 }} />
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Fuera de la explotación
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Ventas, fianzas y movimientos puntuales. No entran en el cálculo de arriba.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Otros cobros
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {formatCurrency(otherReceipts)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Otros pagos
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {formatCurrency(otherPayments)}
              </Typography>
            </Box>
          </Stack>
        </>
      )}
    </Paper>
  );
};
